import DeviceHubIcon from '@mui/icons-material/DeviceHub';
import CloseIcon from '@mui/icons-material/Close';
import {
    Box,
    Chip,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    Typography,
} from '@mui/material';
import { observer } from 'mobx-react';
import React, { useState } from 'react';
import { Processo } from '../modelo';
import { ProcessoAssociado, ProcessoComCorreJunto } from '../modelo/ProcessoRelacionamentos';
import ProcessoApi from '../api/ProcessoApi';
import ProcessoService from '../services/ProcessoService';
import ServidorService from '../services/ServidorService';
import GabineteStore from '../store/GabineteStore';
import MensagemStore from '../store/MensagemStore';
import RouterStore from '../store/RouterStore';
import UsuarioStore from '../store/UsuarioStore';
import NumeroProcesso from '../utils/NumeroProcesso';
import IconeEspelhoProcesso from '../img/icones/IconeEspelhoProcesso';

interface Props {
    processosAssociados: ProcessoAssociado[];
    correJuntos: ProcessoComCorreJunto[];
}

interface DetalhesAssociado {
    processoImportado?: Processo;
    gabinetes: string[];
    gabineteImportado?: string;
}

type EntradaDetalheAssociado = readonly [number | null, DetalhesAssociado];

interface ProcessoRelacionado {
    idProcesso: number | null;
    numeroProcesso: string;
    situacao?: string;
    status?: string;
}

const numeroSemFase = (numeroProcesso: string): string => {
    if (!NumeroProcesso.isValido(numeroProcesso)) {
        return numeroProcesso.replace(/^[A-Z\s-]+/i, '').trim();
    }
    return NumeroProcesso.valueOf(numeroProcesso).semFase().numeroProcessoFormatado();
};

const formatarNome = (nome?: string): string | undefined => {
    if (!nome) return undefined;
    const preposicoes = ['da', 'do', 'das', 'dos', 'a', 'e', 'de'];
    return nome
        .toLowerCase()
        .split(' ')
        .map((parte) => preposicoes.includes(parte) ? parte : parte.charAt(0).toUpperCase() + parte.slice(1))
        .join(' ');
};

const extrairNomeUsuario = (usuario: any): string | undefined => (
    usuario?.nome
    || usuario?.servidor?.nome
    || usuario?.usuario?.nome
    || usuario?.pessoa?.nome
);

const ModalProcessosAssociadosEspelho = observer(({ processosAssociados, correJuntos }: Props) => {
    const [aberta, setAberta] = useState(false);
    const [carregando, setCarregando] = useState(false);
    const [detalhes, setDetalhes] = useState<Record<string, DetalhesAssociado>>({});
    const [nomesResponsaveis, setNomesResponsaveis] = useState<Record<string, string>>({});
    const exibindoCorreJuntos = correJuntos?.length > 0 && !processosAssociados?.length;
    const processosRelacionados: ProcessoRelacionado[] = exibindoCorreJuntos
        ? correJuntos.map((processo) => ({
            idProcesso: processo.id,
            numeroProcesso: numeroSemFase(processo.numProcCompletoCorreJunto)
        }))
        : processosAssociados;

    const carregarDetalhes = async () => {
        setCarregando(true);
        try {
            const idsValidos = processosRelacionados
                .map((processo) => processo.idProcesso)
                .filter((id): id is number => id != null && Number.isFinite(id) && id > 0);
            const importadosNoGabinete = idsValidos.length
                ? await ProcessoService.recuperaPorSearch({
                    gabinete: UsuarioStore.gabinete,
                    idsProcessos: idsValidos
                }).catch(() => [])
                : [];
            const importadosPorId = new Map<number, Processo>(
                importadosNoGabinete.map((processo): readonly [number, Processo] => [processo.identificadorProcesso, processo])
            );

            const entradas: EntradaDetalheAssociado[] = await Promise.all(processosRelacionados.map(async (associado): Promise<EntradaDetalheAssociado> => {
                if (!associado.idProcesso) {
                    return [associado.idProcesso, { gabinetes: [], gabineteImportado: undefined, processoImportado: undefined }] as const;
                }
                let processoImportado = importadosPorId.get(associado.idProcesso);
                let gabineteImportado = processoImportado ? UsuarioStore.gabinete : undefined;
                const gabinetes = processoImportado
                    ? []
                    : await ProcessoApi.buscarGabinetesOndeProcessoEstaImportado(associado.idProcesso).catch(() => []);

                if (!processoImportado) {
                    for (const gabinete of gabinetes) {
                        const processosDoGabinete = await ProcessoService.recuperaPorSearch({
                            gabinete,
                            idsProcessos: [associado.idProcesso]
                        }).catch(() => []);
                        processoImportado = processosDoGabinete[0];
                        if (processoImportado) {
                            gabineteImportado = gabinete;
                            break;
                        }
                    }
                }

                return [associado.idProcesso, { processoImportado, gabinetes, gabineteImportado }] as const;
            }));
            setDetalhes(Object.fromEntries(entradas.map(([k, v]) => [String(k), v])) as Record<string, DetalhesAssociado>);

            const processosImportados = entradas
                .map(([, detalhe]) => detalhe.processoImportado)
                .filter((processo): processo is Processo => !!processo);
            const cpfsResponsaveis = Array.from(new Set(processosImportados
                .map((processo) => processo.numeroCpfResponsavel?.trim().padStart(11, '0'))
                .filter((cpf): cpf is string => !!cpf)));

            const gabinetesImportados = Array.from(new Set(entradas
                .map(([, detalhe]) => detalhe.gabineteImportado)
                .filter((gabinete): gabinete is string => !!gabinete)));
            const listasUsuarios = await Promise.all(gabinetesImportados.map(async (gabinete) => {
                const usuarios = GabineteStore.usarBuscaPorDescricaoLocalizacao(gabinete)
                    ? await ServidorService.buscarServidoresPJeSecretarias(GabineteStore.descricaoLocalizacaoPorSigla(gabinete)).catch(() => [])
                    : await ServidorService.buscarServidoresPJe(gabinete).catch(() => []);
                return usuarios || [];
            }));
            const usuariosDosGabinetes = listasUsuarios.flat();

            const nomesDosGabinetes = cpfsResponsaveis.reduce((resultado, cpf) => {
                const usuario = usuariosDosGabinetes.find((usr) => usr.login?.trim().padStart(11, '0') === cpf);
                const nome = formatarNome(usuario?.nome);
                if (nome) resultado[cpf] = nome;
                return resultado;
            }, {} as Record<string, string>);
            const cpfsNaoEncontrados = cpfsResponsaveis.filter((cpf) => !nomesDosGabinetes[cpf]);
            const usuariosPorCpf = cpfsNaoEncontrados.length
                ? await ServidorService.msConsultaUsuarios(cpfsNaoEncontrados)
                : {};
            const nomesExternos = cpfsNaoEncontrados.reduce((resultado, cpf) => {
                const usuario = usuariosPorCpf?.[cpf];
                const nome = formatarNome(extrairNomeUsuario(usuario));
                if (nome) resultado[cpf] = nome;
                return resultado;
            }, {} as Record<string, string>);
            setNomesResponsaveis({ ...nomesDosGabinetes, ...nomesExternos });
        } catch (error) {
            console.warn('Falha ao carregar detalhes dos processos associados.', error);
            setDetalhes({});
            setNomesResponsaveis({});
        } finally {
            setCarregando(false);
        }
    };

    const abrir = () => {
        setAberta(true);
        carregarDetalhes();
    };

    const nomeResponsavel = (processo?: Processo) => {
        if (!processo?.numeroCpfResponsavel) return 'Sem responsável';
        const cpf = processo.numeroCpfResponsavel.trim().padStart(11, '0');
        return nomesResponsaveis[cpf] || GabineteStore.recuperarNomeServidor(cpf);
    };

    const copiarNumeroProcesso = async (numeroProcesso: string) => {
        try {
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(numeroProcesso);
            } else {
                const textArea = document.createElement('textarea');
                textArea.value = numeroProcesso;
                textArea.style.position = 'fixed';
                textArea.style.opacity = '0';
                document.body.appendChild(textArea);
                textArea.select();
                document.execCommand('copy');
                document.body.removeChild(textArea);
            }
            MensagemStore.apresentarMensagem('Número do processo copiado.');
        } catch (_error) {
            MensagemStore.apresentarErro('Não foi possível copiar o número do processo.');
        }
    };

    const copiarComTeclado = (event: React.KeyboardEvent, numeroProcesso: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            copiarNumeroProcesso(numeroProcesso);
        }
    };

    if (!processosRelacionados.length) return null;

    return (
        <>
            <Tooltip title={exibindoCorreJuntos ? 'Processos corre-juntos' : 'Processos associados'}>
                <IconButton color='inherit' onClick={abrir} aria-label={exibindoCorreJuntos ? 'Processos corre-juntos' : 'Processos associados'}>
                    <DeviceHubIcon sx={{ transform: 'rotate(-90deg)' }} />
                </IconButton>
            </Tooltip>
            <Dialog
                open={aberta}
                onClose={() => setAberta(false)}
                maxWidth='lg'
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        boxShadow: '0 18px 55px rgba(15, 23, 42, 0.22)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        px: 3,
                        py: 2.25,
                        borderBottom: '1px solid',
                        borderColor: 'divider'
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Box
                            sx={{
                                display: 'grid',
                                placeItems: 'center',
                                width: 40,
                                height: 40,
                                borderRadius: 2,
                                color: 'primary.main',
                                bgcolor: 'action.hover'
                            }}
                        >
                            <DeviceHubIcon sx={{ transform: 'rotate(-90deg)' }} />
                        </Box>
                        <Box>
                            <Typography variant='h6' component='div' sx={{ fontWeight: 600, lineHeight: 1.25 }}>
                                {exibindoCorreJuntos ? 'Processos corre-juntos' : 'Processos associados'}
                            </Typography>
                            <Typography variant='body2' color='text.secondary'>
                                {processosRelacionados.length} {processosRelacionados.length === 1 ? 'processo' : 'processos'}
                            </Typography>
                        </Box>
                    </Box>
                    <Tooltip title='Fechar'>
                        <IconButton onClick={() => setAberta(false)} aria-label='Fechar' size='small'>
                            <CloseIcon />
                        </IconButton>
                    </Tooltip>
                </DialogTitle>
                <DialogContent sx={{ p: 3, bgcolor: '#f8fafc' }}>
                    {carregando ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, py: 7 }}>
                            <CircularProgress />
                            <Typography variant='body2' color='text.secondary'>
                                Carregando informações dos processos...
                            </Typography>
                        </Box>
                    ) : (
                        <TableContainer
                            sx={{
                                mt: 2,
                                border: '1px solid',
                                borderColor: 'divider',
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                overflow: 'auto'
                            }}
                        >
                            <Table size='small' sx={{ minWidth: 860 }}>
                                <TableHead>
                                    <TableRow sx={{ bgcolor: '#f1f5f9' }}>
                                        {['Número do processo', ...(!exibindoCorreJuntos ? ['Tipo de associação', 'Status'] : []), 'Movimentação', 'Responsável', 'Processo importado na unidade'].map((titulo) => (
                                            <TableCell
                                                key={titulo}
                                                sx={{ py: 0.75, fontWeight: 600, color: 'text.secondary', whiteSpace: 'nowrap' }}
                                            >
                                                {titulo === 'Processo importado na unidade'
                                                    ? <>Processo importado<br />na unidade</>
                                                    : titulo}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {processosRelacionados.map((associado, index) => {
                                        const detalhe = detalhes[String(associado.idProcesso)];
                                        const processoImportado = detalhe?.processoImportado;
                                        // Movimentação e responsável são informações internas do gabinete.
                                        // Só devem aparecer quando o processo estiver importado no gabinete
                                        // atualmente acessado; a unidade de importação pode continuar visível.
                                        const exibirInformacoesInternas = detalhe?.gabineteImportado === UsuarioStore.gabinete;
                                        return (
                                            <TableRow
                                                key={`${associado.idProcesso}-${associado.situacao || ''}-${associado.status || ''}-${index}`}
                                                hover
                                                sx={{ '&:last-child td': { borderBottom: 0 } }}
                                            >
                                                <TableCell sx={{ py: 1.4, fontWeight: 500, whiteSpace: 'nowrap' }}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        <Tooltip title='Copiar número do processo'>
                                                            <Box
                                                                component='span'
                                                                role='button'
                                                                tabIndex={0}
                                                                onClick={() => copiarNumeroProcesso(associado.numeroProcesso)}
                                                                onKeyDown={(event) => copiarComTeclado(event, associado.numeroProcesso)}
                                                                sx={{
                                                                    color: 'primary.main',
                                                                    cursor: 'pointer',
                                                                    '&:hover': { color: 'primary.dark' },
                                                                    '&:focus-visible': {
                                                                        outline: '2px solid',
                                                                        outlineColor: 'primary.main',
                                                                        outlineOffset: 3,
                                                                        borderRadius: 0.5
                                                                    }
                                                                }}
                                                            >
                                                                {associado.numeroProcesso}
                                                            </Box>
                                                        </Tooltip>
                                                        {associado.idProcesso ? (
                                                            <Tooltip title='Abrir espelho do processo'>
                                                                <IconButton
                                                                    size='small'
                                                                    onClick={() => RouterStore.abrirJanela('/espelhoProcesso/' + associado.idProcesso)}
                                                                    aria-label='Abrir espelho do processo'
                                                                    sx={{ p: 0.5 }}
                                                                >
                                                                    <IconeEspelhoProcesso size={20} />
                                                                </IconButton>
                                                            </Tooltip>
                                                        ) : (
                                                            <Tooltip title='Processo não PJe'>
                                                                <span>
                                                                    <IconButton
                                                                        size='small'
                                                                        disabled
                                                                        aria-label='Processo não PJe'
                                                                        sx={{ p: 0.5, opacity: 0.4 }}
                                                                    >
                                                                        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
                                                                            <IconeEspelhoProcesso size={20} />
                                                                            <Box
                                                                                sx={{
                                                                                    position: 'absolute',
                                                                                    top: '50%',
                                                                                    left: '-2px',
                                                                                    right: '-2px',
                                                                                    height: '2px',
                                                                                    bgcolor: 'text.disabled',
                                                                                    transform: 'rotate(-45deg)',
                                                                                    transformOrigin: 'center',
                                                                                }}
                                                                            />
                                                                        </Box>
                                                                    </IconButton>
                                                                </span>
                                                            </Tooltip>
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                {!exibindoCorreJuntos && (
                                                    <TableCell>
                                                        <Chip label={associado.situacao || 'Não informada'} size='small' variant='outlined' />
                                                    </TableCell>
                                                )}
                                                {!exibindoCorreJuntos && (
                                                    <TableCell>
                                                        <Chip label={associado.status || 'Não informado'} size='small' variant='outlined' />
                                                    </TableCell>
                                                )}
                                                <TableCell sx={{ maxWidth: 220 }}>
                                                    {exibirInformacoesInternas && processoImportado?.controleGabinete?.descricao
                                                        ? <Typography
                                                            variant='body2'
                                                            sx={{ color: 'text.primary', whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.3 }}
                                                        >
                                                            {processoImportado.controleGabinete.descricao}
                                                        </Typography>
                                                        : <Typography color='text.disabled'>—</Typography>}
                                                </TableCell>
                                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                    {exibirInformacoesInternas && processoImportado
                                                        ? nomeResponsavel(processoImportado)
                                                        : <Typography color='text.disabled'>—</Typography>}
                                                </TableCell>
                                                <TableCell>
                                                    {(processoImportado || detalhe?.gabinetes?.length)
                                                        ? <Chip
                                                            label={detalhe?.gabineteImportado || detalhe?.gabinetes?.join(', ')}
                                                            size='small'
                                                            color='success'
                                                            variant='outlined'
                                                        />
                                                        : <Typography variant='body2' color='text.secondary'>Não importado</Typography>}
                                                </TableCell>
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}
                </DialogContent>
            </Dialog>
        </>
    );
});

export default ModalProcessosAssociadosEspelho;
